import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createAsyncThunk } from "@reduxjs/toolkit";
import client from "../../../app/core/client-http/client";
import { retrieveToken } from "../../../utils/storage";
import { endpoint } from "../../../utils/constants/endpoint";

export const sendMessage = createAsyncThunk(
  "messages/send",
  async ({ practiceId, message }) => {
    const endpoint = `/admin/practices/${practiceId}/messages`;
    const response = await client.post(endpoint, {
      Message: message, // Invia il messaggio come parte di un oggetto con la chiave 'Message'
    });
    return response.data;
  }
);

export const addNotes = createAsyncThunk(
  "addNotes/update",
  async ({ practiceId, notes }) => {
    const response = await client.put(`/admin/practices/${practiceId}`, {
      OperatorNotes: notes,
    });
    return response.data;
  }
);

//CHIAMATE GET

export const adminPracticesApi = createApi({
  reducerPath: "adminPractices",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["practices", "documents", "alluserdocuments"],
  endpoints: (builder) => ({
    getPracticeManagement: builder.query({
      query: ({
        page,
        userFilter,
        filter = {
          status: "",
          serviceId: "",
          studioId: "",
          adminId: "",
          companyId: "",
          queryName: "",
        },
        orderDirection,
        valueToOrderBy,
        showAll,
        queryName = "",
      }) => ({
        url: `admin/practices/list?query=${userFilter.trim()}&status=${filter.status
          }&serviceId=${filter.serviceId}&showAll=${showAll}&studioId=${filter.studioId
          }&adminId=${filter.adminId}&userCompanyId=${filter.companyId
          }&queryName=${queryName}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: {
          startRow: page.startRow,
          endRow: page.endRow,
          sort: [
            {
              colId: valueToOrderBy,
              sortDirection: orderDirection,
              sortIndex: 0,
            },
          ],
        },
      }),
      providesTags: ["practices"],
    }),
    getAllUserDocuments: builder.query({
      query: ({
        id,
        filter = {
          mainCategoryId: "",
          categoryId: "",
          exclusive: false,
        },
        page,
        query = "",
      }) => ({
        url: `/admin/practices/${id}/alluserdocuments?query=${query}&mainCategoryId=${filter.mainCategoryId}&categoryId=${filter.categoryId}&exclusive=${filter.exclusive}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: { startRow: page.startRow, endRow: page.endRow },
      }),
      providesTags: ["alluserdocuments", "documents"],
    }),
    getPracticeById: builder.query({
      query: (id) => ({
        url: "admin/practices/" + id,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["practice"],
    }),
    createPractice: builder.mutation({
      query: (practice) => ({
        url: `/admin/practices`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: practice,
      }),
      invalidatesTags: ["practices"],
    }),
    deletePractice: builder.mutation({
      query: (practiceId) => ({
        url: `/admin/practices/${practiceId}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      invalidatesTags: ["practices"],
    }),
    getPracticesServices: builder.query({
      query: () => ({
        url: `${endpoint.ADMIN_PRACTICE_SERVICE.SERVICES}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: { startRow: 0, endRow: 100 },
      }),
    }),
    getDocuments: builder.query({
      query: (id) => ({
        url: "admin/practices/" + id + "/doccategories",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["documents"],
    }),
    setStatus: builder.mutation({
      query: ({ practiceId, status }) => ({
        url: `/admin/practices/${practiceId}/setstatus?status=${status}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: { practiceId, status },
      }),
      invalidatesTags: ["practice", "practices"],
    }),
    createPracticeDocument: builder.mutation({
      query: ({ cleanedDocumentData, practiceId }) => ({
        url: `/admin/practices/${practiceId}/newdocument`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: cleanedDocumentData,
      }),
      invalidatesTags: ["documents"],
    }),
    editAdminPractices: builder.mutation({
      query: ({ adminId, practiceId }) => ({
        url: `/admin/practices/${practiceId}/setadmin`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: { adminId },
      }),
      invalidatesTags: ["practice", "practices"],
    }),
    serviceDocComposition: builder.mutation({
      query: (idPratica) => ({
        url: `admin/practices/${idPratica}/doccomposition`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    addAssociateDocument: builder.mutation({
      query: (body) => ({
        url: `/admin/practices/${body.id}/documents`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    deleteAssociateDocument: builder.mutation({
      query: (params) => ({
        url: `/admin/practices/${params.practiceId}/documents/${params.documentId}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: params,
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    doRefreshDocs: builder.mutation({
      query: (idPratica) => ({
        url: `admin/practices/${idPratica}/autorefreshdocs`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: idPratica,
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
  }),
});

export const {
  useGetPracticeManagementQuery,
  useGetPracticesServicesQuery,
  useGetAllUserDocumentsQuery,
  useCreatePracticeMutation,
  useGetPracticeByIdQuery,
  useGetDocumentsQuery,
  useSetStatusMutation,
  useCreatePracticeDocumentMutation,
  useDeletePracticeMutation,
  useEditAdminPracticesMutation,
  useServiceDocCompositionMutation,
  useAddAssociateDocumentMutation,
  useDeleteAssociateDocumentMutation,
  useDoRefreshDocsMutation,
} = adminPracticesApi;
